import React, { useEffect, useMemo, useRef } from 'react';

import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  OutlinedInput,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Clear as ClearIcon, Help as HelpIcon } from '@material-ui/icons';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import FormDateInput from 'components/FormDateInput';
import FormRichTextInput from 'components/FormRichTextInput';
import FormSelect from 'components/FormSelect';
import FormTextInput from 'components/FormTextInput';
import Loader from 'components/Loader';
import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import { ActionTemplate } from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';

import { ActionLibraryInstanceFormValues } from '../../ActionLibraryInstanceForm.types';

import useStyles from './styles/StepTimeParameters.styles';

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 400,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

const StepTimeParameters: React.FC = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched } = useFormikContext<ActionLibraryInstanceFormValues>();

  const prefilledValidityForTemplateId = useRef<string | null>(null);

  const { data: choicesData } = useQuery('actionTemplatesChoices', () => actionTemplatesApi.getChoices(), { staleTime: 1000 * 60 * 10 });

  const criticalityOptions = useMemo(() => {
    if (choicesData?.data?.criticality?.length) {
      return choicesData.data.criticality.map((c: { value: string; label: string }) => ({
        key: c.value,
        label: c.label,
      }));
    }
    return [
      { key: 'low', label: t(action_library_messages.criticality.LOW) },
      { key: 'medium', label: t(action_library_messages.criticality.MEDIUM) },
      { key: 'high', label: t(action_library_messages.criticality.HIGH) },
    ];
  }, [choicesData?.data?.criticality, t]);

  const { data: templateResponse, isLoading } = useQuery(
    ['ACTION_TEMPLATES_GET_SINGLE', values.templateId],
    () => actionTemplatesApi.getSingleActionTemplate(Number(values.templateId)),
    { enabled: !!values.templateId },
  );

  // Prefill validity period (months, int) from template; user can set 0 or clear to opt out
  useEffect(() => {
    const months = templateResponse?.data?.validityPeriodMonths;
    if (months != null && values.validityPeriodMonths === null && prefilledValidityForTemplateId.current !== values.templateId) {
      setFieldValue('validityPeriodMonths', months);
      prefilledValidityForTemplateId.current = values.templateId;
    }
    if (!values.templateId) prefilledValidityForTemplateId.current = null;
  }, [templateResponse?.data?.validityPeriodMonths, values.templateId, values.validityPeriodMonths, setFieldValue]);

  const validityDoesNotApply = values.validityPeriodMonths === null;
  const handleValidityCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('validityPeriodMonths', e.target.checked ? null : values.validityPeriodMonths ?? 0);
  };

  const renderMetadataTooltip = (template: ActionTemplate) => (
    <Box p={1}>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Typography component='div' variant='caption'>
            <strong>{t(action_library_messages.table.purpose)}:</strong> {template.purposeDisplay}
          </Typography>
        </Grid>
        {template.subAreaData && (
          <Grid item xs={6}>
            <Typography component='div' variant='caption'>
              <strong>{t(action_library_messages.table.subarea)}:</strong> {template.subAreaData.name}
            </Typography>
          </Grid>
        )}
        <Grid item xs={6}>
          <Typography component='div' variant='caption'>
            <strong>{t(action_library_messages.instance.form.owner_type)}:</strong> {template.ownerTypeDisplay}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography component='div' variant='caption'>
            <strong>{t(action_library_messages.table.criticality)}:</strong> {template.criticalityDisplay}
          </Typography>
        </Grid>
        {template.reference && (
          <Grid item xs={12}>
            <Typography component='div' variant='caption'>
              <strong>{t(action_library_messages.detail.reference)}:</strong> {template.reference}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  if (isLoading) return <Loader />;

  return (
    <Box>
      <Box className={styles.section}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormTextInput
              formik={useFormikContext()}
              fullWidth
              id='instanceName'
              label={t(action_library_messages.instance.form.instance_name)}
              required
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <FormDateInput
              dateTime={false}
              formik={useFormikContext()}
              fullWidth
              id='deadlineDate'
              label={t(action_library_messages.instance.form.deadline)}
              required
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <Box alignItems='flex-start' display='flex' position='relative'>
              <FormDateInput
                dateTime={false}
                formik={useFormikContext()}
                fullWidth
                id='reminderDate'
                label={t(action_library_messages.instance.form.reminder)}
              />
              {values.reminderDate && (
                <Tooltip title={t(action_library_messages.instance.form.clear_reminder)}>
                  <IconButton
                    aria-label='Clear reminder'
                    onClick={() => {
                      setFieldValue('reminderDate', '');
                      setFieldValue('reminderTime', '16:00');
                    }}
                    size='small'
                    style={{
                      position: 'absolute',
                      right: 4,
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    <ClearIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>
          <Grid item xs={4}>
            <div className={styles.inlineFields}>
              <div style={{ flex: 2 }}>
                <FormControl fullWidth variant='outlined'>
                  <InputLabel htmlFor='validityPeriodMonths'>{t(action_library_messages.properties.duration_in_months)}</InputLabel>
                  <OutlinedInput
                    disabled={validityDoesNotApply}
                    id='validityPeriodMonths'
                    inputProps={{ min: 0, step: 1, type: 'number' }}
                    label={t(action_library_messages.properties.duration_in_months)}
                    name='validityPeriodMonths'
                    onBlur={() => setFieldTouched('validityPeriodMonths', true)}
                    onChange={e => {
                      const v = e.target.value;
                      setFieldValue('validityPeriodMonths', v === '' ? null : Math.max(0, parseInt(String(v), 10) || 0));
                    }}
                    value={values.validityPeriodMonths ?? ''}
                  />
                </FormControl>
              </div>
              <div style={{ flex: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={validityDoesNotApply}
                      id='validityPeriodDoesNotApply'
                      name='validityPeriodDoesNotApply'
                      onChange={handleValidityCheckboxChange}
                    />
                  }
                  label={t(action_library_messages.properties.doesnt_apply)}
                />
              </div>
            </div>
          </Grid>
          <Grid item md={4} xs={12}>
            <FormTextInput
              formik={useFormikContext()}
              fullWidth
              id='reportedTo'
              label={t(action_library_messages.instance.form.reported_to)}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <FormSelect
              formik={useFormikContext()}
              fullWidth
              id='criticality'
              label={t(action_library_messages.table.criticality)}
              options={criticalityOptions}
              size='small'
            />
          </Grid>
        </Grid>
      </Box>

      <Box className={styles.section}>
        <Typography style={{ marginBottom: '15px' }} variant='h6'>
          {t(action_library_messages.instance.form.special_instruction)}
        </Typography>

        <FormRichTextInput formik={useFormikContext() as any} id='specialInstruction' />
      </Box>
      <Box className={styles.section}>
        <Box className={styles.headerWithInfo}>
          <Typography gutterBottom variant='subtitle2'>
            {t(action_library_messages.instance.form.template_summary)}
          </Typography>
          {templateResponse?.data && (
            <HtmlTooltip title={renderMetadataTooltip(templateResponse.data)}>
              <HelpIcon className={styles.infoIcon} />
            </HtmlTooltip>
          )}
        </Box>
        <Paper className={styles.summaryPaper} variant='outlined'>
          <Typography
            color='textSecondary'
            dangerouslySetInnerHTML={{ __html: templateResponse?.data.shortDescription || '' }}
            variant='body2'
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default StepTimeParameters;
